import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useRelayEnvironment } from 'react-relay';

import Avatar from '@atlaskit/avatar';

import { useContentRefetchDispatch } from '@confluence/content-body/entry-points/ContentRefetchContext.ts';
import { fetchQuery } from '@confluence/apollo-relay-interop/entry-points/fetchQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	useAllCommentsData,
	getCommentsDataFromCache,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { getProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';

import { useInlineReactionActions } from '../inlineReactionStore.ts';

import type {
	InlineReactionsPubSubHandlerActiveDataRefetchQuery$data,
	InlineReactionsPubSubHandlerActiveDataRefetchQuery$variables,
} from './__generated__/InlineReactionsPubSubHandlerActiveDataRefetchQuery.graphql.ts';
import type {
	InlineReactionsPubSubHandlerActorQuery$data,
	InlineReactionsPubSubHandlerActorQuery$variables,
} from './__generated__/InlineReactionsPubSubHandlerActorQuery.graphql.ts';
import type {
	InlineReactionsPubSubHandlerSummaryRefetchQuery$data,
	InlineReactionsPubSubHandlerSummaryRefetchQuery$variables,
} from './__generated__/InlineReactionsPubSubHandlerSummaryRefetchQuery.graphql.ts';
import { extractNumericCommentId } from '../utils/extractNumericCommentId.ts';
import {
	removeInlineReactionsPubSub,
	setUpInlineReactionsPubSub,
	type InlineReactionsContentType,
} from './inlineReactionsPubSub.ts';

// Three dedicated queries for the pubsub handler. They mirror the queries
// owned by `useReactionsSummary`, `useActiveInlineData`, and `useReactedUsers`
// but are declared here so the handler does not depend on those hooks'
// codegen artefacts. CI's Relay codegen will produce the matching
// `__generated__/...graphql.ts` files on first build.

const inlineReactionsPubSubHandlerSummaryRefetchQuery = graphql`
	query InlineReactionsPubSubHandlerSummaryRefetchQuery(
		$cloudId: ID!
		$contentId: ID!
		$contentType: GraphQLReactionContentType!
	) {
		confluence_contentReactionsSummary(
			cloudId: $cloudId
			contentId: $contentId
			contentType: $contentType
		) {
			reactionsSummary {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					emojiId
					count
					reacted
				}
			}
		}
	}
`;

const inlineReactionsPubSubHandlerActiveDataRefetchQuery = graphql`
	query InlineReactionsPubSubHandlerActiveDataRefetchQuery($cloudId: ID!, $contentId: ID!) {
		confluence_activeInlineData(cloudId: $cloudId, contentId: $contentId) {
			reactions {
				commentId
				markerRef
				type
			}
		}
	}
`;

const inlineReactionsPubSubHandlerActorQuery = graphql`
	query InlineReactionsPubSubHandlerActorQuery(
		$cloudId: ID!
		$input: ConfluenceReactedUsersInput!
	) {
		confluence_reactedUsers(cloudId: $cloudId, input: $input) {
			confluencePerson {
				accountId
				displayName
				profilePicture {
					path
				}
			}
		}
	}
`;

const i18n = defineMessages({
	newReactionFlagTitle: {
		id: 'experiment-inline-reactions.pubsub.new-reaction-flag-title',
		defaultMessage: 'New reaction',
		description:
			'Title of the notification flag shown when another user adds an inline reaction to a page or blog post the current user is viewing.',
	},
	somebodyReactedToPage: {
		id: 'experiment-inline-reactions.pubsub.somebody-reacted-to-page',
		defaultMessage: '{somebody} reacted to this page',
		description:
			'Description text in the notification flag shown when another user adds an inline reaction to a page the current user is viewing. {somebody} is the actor display name.',
	},
	somebodyReactedToBlog: {
		id: 'experiment-inline-reactions.pubsub.somebody-reacted-to-blog',
		defaultMessage: '{somebody} reacted to this blog post',
		description:
			'Description text in the notification flag shown when another user adds an inline reaction to a blog post the current user is viewing. {somebody} is the actor display name.',
	},
	someoneFallback: {
		id: 'experiment-inline-reactions.pubsub.someone-fallback',
		defaultMessage: 'Someone',
		description:
			'Fallback text used in place of an actor display name when the user that produced the inline reaction event is unknown.',
	},
});

export type InlineReactionsPubSubHandlerProps = {
	contentId: string;
	contentType?: InlineReactionsContentType;
};

type Props = InlineReactionsPubSubHandlerProps & WithFlagsProps;

const InlineReactionsPubSubHandlerComponent: React.FC<Props> = ({
	contentId,
	contentType = 'page',
	flags,
}) => {
	const intl = useIntl();
	const { cloudId, userId } = useSessionData();
	const relayEnvironment = useRelayEnvironment();
	const { addOptimisticReaction, pruneOptimisticReactions, applyRemoteDelta } =
		useInlineReactionActions();
	const { addCommentInCache } = useAllCommentsData({ contentId, options: { skip: false } });
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();
	const { commentService } = useCommentService();
	const dispatchContext = useSubscriptionsDispatchContext();
	const { setRefetchContent } = useContentRefetchDispatch();
	const subscriptionsRef = useRef<Array<{ subscriberId: string; delta: string }>>([]);

	const markDocumentNeedsUpdate = useCallback(() => {
		// Trigger the page-body refetch via `ContentRefetchContext` (the same
		// hook the inline-comments pubsub flow uses on
		// `setDocumentUpdated(true); setRefetchContent(true)`). Receiver
		// re-pulls the published ADF; the new annotation mark arrives in the
		// loaded doc; the renderer's `mapRendererAnnotationStates` then sees
		// the marker in both the doc AND `reactionMarkerRefs` and paints the
		// highlight. See `onInlineReactionAdded` for the full Option-A
		// rationale.
		// TODO ACTIVATION-2629: replace with `rendererActions.applyAnnotation()`
		setRefetchContent(true);
	}, [setRefetchContent]);

	// Re-issue the same query nodes that `useReactionsSummary` and
	// `useActiveInlineData` use. `fetchQuery` writes the response into the
	// shared Relay/Apollo cache against the same key those hooks watch, so
	// any active subscriber instance re-renders with the fresh data
	// automatically. This is the supported pattern used elsewhere (e.g.
	// `MissedPubSubCommentEventsHandler` calling `usePageInfo({...}).refetch`)
	// and avoids low-level cache surgery via `client.cache.evict`.

	const refetchReactionsSummary = useCallback(
		(commentId: string) => {
			const variables: InlineReactionsPubSubHandlerSummaryRefetchQuery$variables = {
				cloudId,
				contentId: extractNumericCommentId(commentId),
				contentType: 'COMMENT',
			};
			fetchQuery<{
				response: InlineReactionsPubSubHandlerSummaryRefetchQuery$data;
				variables: InlineReactionsPubSubHandlerSummaryRefetchQuery$variables;
			}>(relayEnvironment, inlineReactionsPubSubHandlerSummaryRefetchQuery, variables).subscribe({
				error: () => {
					// Network failure — leave the cached entry as-is. A
					// subsequent user-initiated refetch will recover.
				},
			});
		},
		[cloudId, relayEnvironment],
	);

	const refetchActiveInlineData = useCallback(() => {
		// `confluence_activeInlineData` expects a content (page/blogpost) ARI,
		// not the comment ARI. Build it from the resource type the handler
		// was mounted with.
		const resource = contentType === 'blogpost' ? 'blogpost' : 'page';
		const variables: InlineReactionsPubSubHandlerActiveDataRefetchQuery$variables = {
			cloudId,
			contentId: `ari:cloud:confluence:${cloudId}:${resource}/${contentId}`,
		};
		fetchQuery<{
			response: InlineReactionsPubSubHandlerActiveDataRefetchQuery$data;
			variables: InlineReactionsPubSubHandlerActiveDataRefetchQuery$variables;
		}>(relayEnvironment, inlineReactionsPubSubHandlerActiveDataRefetchQuery, variables).subscribe({
			error: () => {
				// Network failure — leave the cached entry as-is.
			},
		});
	}, [cloudId, contentId, contentType, relayEnvironment]);

	const showReactionFlag = useCallback(
		({
			commentId,
			emojiId,
			actorAccountId,
			contentType: flagContentType,
		}: {
			commentId: string;
			markerRef: string;
			emojiId?: string;
			contentType: InlineReactionsContentType;
			actorAccountId?: string;
		}) => {
			const buildDescription = (somebody: string) =>
				intl.formatMessage(
					flagContentType === 'blogpost' ? i18n.somebodyReactedToBlog : i18n.somebodyReactedToPage,
					{ somebody },
				);

			const fallbackName = intl.formatMessage(i18n.someoneFallback);
			const flagId = `inline-reactions-pubsub-${commentId}`;

			const presentFlag = (somebody: string, avatarSrc?: string) => {
				void flags.showCustomFlag({
					id: flagId,
					title: intl.formatMessage(i18n.newReactionFlagTitle),
					description: buildDescription(somebody),
					isAutoDismiss: true,
					...(avatarSrc
						? { customIcon: <Avatar src={avatarSrc} name={somebody} size="small" /> }
						: {}),
				});
			};

			// If we don't have enough info to resolve the actor name, just show
			// the fallback flag immediately — there's nothing to wait on.
			if (!emojiId || !actorAccountId) {
				presentFlag(fallbackName);
				return;
			}

			const containerType = contentType === 'blogpost' ? 'BLOGPOST' : 'PAGE';
			const variables: InlineReactionsPubSubHandlerActorQuery$variables = {
				cloudId,
				input: {
					contentId: extractNumericCommentId(commentId),
					contentType: 'COMMENT',
					containerId: contentId,
					containerType,
					emojiId,
				},
			};

			fetchQuery<{
				response: InlineReactionsPubSubHandlerActorQuery$data;
				variables: InlineReactionsPubSubHandlerActorQuery$variables;
			}>(relayEnvironment, inlineReactionsPubSubHandlerActorQuery, variables).subscribe({
				next: (data) => {
					const persons = data?.confluence_reactedUsers?.confluencePerson ?? [];
					const actor = persons.find((p): p is NonNullable<typeof p> =>
						Boolean(p?.accountId && p.accountId === actorAccountId),
					);
					const displayName = actor?.displayName?.trim();
					const avatarSrc = actor?.profilePicture?.path ?? undefined;
					presentFlag(displayName || fallbackName, avatarSrc);
				},
				error: () => {
					presentFlag(fallbackName);
				},
			});
		},
		[cloudId, contentId, contentType, flags, intl, relayEnvironment],
	);

	const memoisedDispatch = useMemo(
		() => ({
			addSubscriber: dispatchContext?.addSubscriber,
			removeSubscriber: dispatchContext?.removeSubscriber,
			setContentId: dispatchContext?.setContentId,
			setContentType: dispatchContext?.setContentType,
		}),
		[dispatchContext],
	);

	useEffect(() => {
		// `confluence_enable_subscriptions-context-provider` is at 100% rollout
		// (cleanup phase), so we don't gate on it here — adding a new use of a
		// stale gate would just have to be cleaned up again. The dispatch
		// helpers below are the practical signal that the central subscription
		// is wired up.
		if (!expValEquals('confluence_inline_reactions', 'isEnabled', true)) {
			return;
		}
		if (
			!memoisedDispatch.addSubscriber ||
			!memoisedDispatch.removeSubscriber ||
			!memoisedDispatch.setContentId ||
			!memoisedDispatch.setContentType
		) {
			return;
		}
		if (!contentId) {
			return;
		}

		subscriptionsRef.current = setUpInlineReactionsPubSub({
			contentId,
			contentType,
			currentUserId: userId,
			addOptimisticReaction,
			pruneOptimisticReactions,
			applyRemoteDelta,
			refetchReactionsSummary,
			refetchActiveInlineData,
			markDocumentNeedsUpdate,
			showReactionFlag,
			addCommentInCache,
			fetchCommentById,
			updateUnreadCommentIDSet,
			getCommentsDataFromCache,
			getProcessedCommentsDataActions,
			triggerCommentPanelRefresh: () =>
				void commentService.triggerCommentPanelRefresh().catch(() => {}),
			addSubscriber: memoisedDispatch.addSubscriber,
			setContentId: memoisedDispatch.setContentId,
			setContentType: memoisedDispatch.setContentType,
		});

		return () => {
			removeInlineReactionsPubSub({
				subscriptions: subscriptionsRef.current,
				removeSubscriber: memoisedDispatch.removeSubscriber,
			});
			subscriptionsRef.current = [];
		};
	}, [
		contentId,
		contentType,
		userId,
		addOptimisticReaction,
		pruneOptimisticReactions,
		applyRemoteDelta,
		refetchReactionsSummary,
		refetchActiveInlineData,
		markDocumentNeedsUpdate,
		showReactionFlag,
		addCommentInCache,
		updateUnreadCommentIDSet,
		commentService,
		memoisedDispatch,
	]);

	return null;
};

InlineReactionsPubSubHandlerComponent.displayName = 'InlineReactionsPubSubHandler';

export const InlineReactionsPubSubHandler = withFlags(InlineReactionsPubSubHandlerComponent);
